import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import {
  SyncSpinner, IconEdit, IconTemplateSave, IconTemplateApply, IconCleanup, IconDiscovery, IconExpandAll,
  IconCollapseAll, IconHistory, IconPlantSettings, IconSchemaEdit, IconTelemetry, IconTable, IconNodeAdd, IconOem,
} from '../icons/HvacIcons';
import { ToolButton, ToolGroup } from '../components/ui/ToolButton';
import { OemDeviceTree } from '../components/OemDeviceTree';
import { canUseRemoteService } from '../components/serviceRemoteApi';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import type {
  PlantSchemaResponse,
  DeviceDto,
  NodeDto,
  LeafDto,
} from '../api/types';
import {
  hydratePlantSnapshot,
  markPlantHydrated,
  releaseSnapshotFetch,
  tryClaimSnapshotFetch,
  useLivePlant,
  type PlantLiveState,
} from '../realtime/signalStore';
import type { GetValuesSnapshotResponse } from '../api/types';
import { useSchemaViewSettings, type SchemaViewSettings } from '../hooks/useSchemaViewSettings';
import { LeafAlarmMarker } from '../components/LeafAlarmMarker';
import { LeafValueDisplay } from '../components/LeafValueDisplay';
import { LeafHistoryChart } from '../components/LeafHistoryChart';
import { FreshnessLegend } from '../components/FreshnessLegend';
import { SendCommandModal } from '../components/SendCommandModal';
import { LeafConfigModal, type LeafPatch, type MoveOption } from '../components/LeafConfigModal';
import { PlantMetadataModal } from '../components/PlantMetadataModal';
import { NodeEditModal, type NodePatch } from '../components/NodeEditModal';
import { AddNodeModal, type AddedNode } from '../components/AddNodeModal';
import { AddLeafModal, type AddedLeaf } from '../components/AddLeafModal';
import { DeviceEditModal } from '../components/DeviceEditModal';
import { PublishSchemaTemplateModal } from '../components/PublishSchemaTemplateModal';
import { ApplySchemaTemplateModal } from '../components/ApplySchemaTemplateModal';
import { RecognitionModal } from '../components/RecognitionModal';
import { RegisterMapModal } from '../components/RegisterMapModal';
import {
  TelemetrySetEditorModal,
  type TelemetrySetDetail,
} from '../components/TelemetrySetEditorModal';
import {
  BindTelemetrySetModal,
  type TelemetryBindingOutcome,
} from '../components/BindTelemetrySetModal';
import { PlantSubNav } from '../components/PlantSubNav';
import { sessionSignal } from '../auth/session';
import { onLeafSchemaUpdated, onSchemaChanged, onSchemaAcked } from '../realtime/hubClient';
import { useLocation } from 'preact-iso';
import '../components/hv-modal.css';
import './PlantSchemaPage.css';

interface EditCtl {
  canEdit: boolean;
  /** Schema-Bearbeiten-Modus: wenn true, zeigen NodeRow/LeafRow einen
   *  Drag-Adorner + Pencil-Rename-Button, und die Zeilen werden zu HTML5-
   *  Drop-Targets. Toggle sitzt in der Baum-Toolbar. */
  editMode: boolean;
  /** Umbenennen (DisplayName) live vom Row-Level. Sofort-PATCH; UI patcht
   *  optimistisch, Fehler rollen zurück per Refetch. */
  renameDisplay(kind: 'node' | 'leaf', id: string, newName: string): Promise<void>;
  /** Reparent per Drag & Drop. targetParentId=null => direkt unter Device. */
  reparent(kind: 'node' | 'leaf', id: string, targetParentId: string | null): Promise<void>;
  /** Iterativ single-leaf-nodes in Parent liften und leere Nodes löschen. */
  optimize(deviceId: string): Promise<void>;
  /** Löscht Node (inkl. Sub-Tree) oder Leaf. Prompt vorher — irreversibel. */
  deleteEntity(kind: 'node' | 'leaf', id: string, label: string): Promise<void>;
  /** Multi-Select: Leaf-Checkbox-State + Bulk-Delete. Nur Leaves —
   *  Nodes werden weiterhin einzeln über Trash-Icon (mit Sub-Tree) gelöscht. */
  isLeafSelected(id: string): boolean;
  toggleLeafSelected(id: string): void;
  selectedCount: number;
  clearSelection(): void;
  bulkDeleteSelected(): Promise<void>;
  openAddNode(parent: { deviceId: string; parentNodeId: string | null; parentLabel: string }): void;
  openAddLeaf(parent: { parentNodeId: string; parentLabel: string }): void;
  openNodeEdit(node: NodeDto): void;
  openDeviceEdit(device: DeviceDto): void;
  /** Publish-as-Schema-Template: sichtbar wenn canEditSchema und ein Device
   *  im Baum sichtbar ist. Öffnet PublishSchemaTemplateModal für dieses
   *  Device. */
  openPublishTemplate(device: DeviceDto): void;
  /** Apply-Schema-Template: sichtbar wenn canEditSchema. Öffnet den
   *  ApplySchemaTemplateModal für dieses Device. Nützlich für Serial-Bus-
   *  Devices die keinen ScanCandidate-Eintrag haben und daher im Devices-
   *  Tab (der nur ScanCandidates listet) unsichtbar sind — Template-Apply
   *  aus der Datenpunkte-Ansicht ist der einzige Weg. */
  openApplyTemplate(device: DeviceDto): void;
  /** Anlagenerkennung: Konfiguration des Reglers lesen und daraus das Schema
   *  vorschlagen (docs/anlagenerkennung.md). */
  openRecognition(device: DeviceDto): void;
  /** Telemetrieset an diesen Regler binden (Konzept §4.3). Der Dialog zeigt
   *  vor dem Anwenden die Auflösungswarnungen und den Lizenzstand — beides
   *  wird beim Speichern durchgesetzt, und es erst dort zu erfahren wäre
   *  eine vermeidbare Sackgasse. */
  openBindTelemetry(device: DeviceDto): void;
  /** Telemetrieset anlegen bzw. bearbeiten. Der Baum des Reglers wird
   *  mitgegeben, damit sich Symbolpfade übernehmen lassen — übernommen wird
   *  der PFAD, nicht die Datenpunkt-Id: sonst wäre das Set nicht
   *  übertragbar. */
  openTelemetrySetEditor(device: DeviceDto): void;
  /** Registertabelle (OEM-Schema) für den Servicezugang, unabhängig vom
   *  Betriebsschema (docs/servicezugang-oem-und-fern.md). Sichtbar mit
   *  plants.admin oder Schema-Recht; speichern nur mit plants.admin. */
  canRegisterMap: boolean;
  /** OEM-Datenpunkte über den Fernzugang (service.fachmann oder service.oem). */
  canOem: boolean;
  openRegisterMap(device: DeviceDto): void;
}

/** Which leaf's Send-modal is currently open (single modal at a time), or null.
 *  Threaded down through NodeRow / LeafRow via context prop so we don't have
 *  to hoist plantId onto every leaf. */
interface CommandModalCtl {
  openFor: (leaf: LeafDto) => void;
}

/** Same-shape control for the schema-edit modal. `canEdit` gates whether the
 *  wrench icon is even rendered — the endpoint still enforces the permission
 *  server-side, but the UI hides the affordance to avoid a fake button. */
interface LeafConfigCtl {
  openFor: (leaf: LeafDto) => void;
  canEdit: boolean;
}

interface Props {
  id?: string;
}

/** Broadcast command from the toolbar. Every NodeRow reacts to a `generation`
 *  bump by resetting its local open-state to `allOpen`. Individual toggles
 *  after that keep working without another expand-all bump. */
interface TreeControl {
  generation: number;
  allOpen: boolean;
}

export function PlantSchemaPage({ id }: Props) {
  // Bumped after an Apply-Schema roundtrip so the tree refetches and picks up
  // the freshly re-parented leaves + newly created group nodes.
  const [refreshTick, setRefreshTick] = useState(0);
  const state = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${id}/schema`, signal),
    [id, refreshTick],
  );

  // Refcount-managed subscription. Hook is always called (rules-of-hooks); the
  // hook is a no-op when id is missing / the fetch is still loading.
  const live = useLivePlant(id);

  // Local, mutable copy of the plant schema so LeafConfigModal saves + hub
  // `schema.leaf.updated` events can patch fields in place without a full
  // refetch. Seeded from useAsync's result the first time it lands, then
  // owned entirely by this component.
  const [schemaOverride, setSchemaOverride] = useState<PlantSchemaResponse | null>(null);
  useEffect(() => {
    if (state.status === 'ok') setSchemaOverride(state.data);
  }, [state.status === 'ok' ? state.data : null]);

  const [configLeafId, setConfigLeafId] = useState<string | null>(null);
  const [showPlantEdit, setShowPlantEdit] = useState(false);
  const [nodeEdit, setNodeEdit] = useState<NodeDto | null>(null);
  const [addNodeParent, setAddNodeParent] = useState<{ deviceId: string; parentNodeId: string | null; parentLabel: string } | null>(null);
  const [addLeafParent, setAddLeafParent] = useState<{ parentNodeId: string; parentLabel: string } | null>(null);
  const [deviceEdit, setDeviceEdit] = useState<DeviceDto | null>(null);
  const [publishTemplateDevice, setPublishTemplateDevice] = useState<DeviceDto | null>(null);
  const [applyTemplateDevice, setApplyTemplateDevice] = useState<DeviceDto | null>(null);
  const [recognitionDevice, setRecognitionDevice] = useState<DeviceDto | null>(null);
  const [bindTelemetryDevice, setBindTelemetryDevice] = useState<DeviceDto | null>(null);
  const [telemetrySetEditorDevice, setTelemetrySetEditorDevice] = useState<DeviceDto | null>(null);
  const [registerMapDevice, setRegisterMapDevice] = useState<DeviceDto | null>(null);
  // Hoehe der Filterleiste als CSS-Variable: die Koepfe der Geraete kleben
  // direkt darunter (Patrick, 22.10.2026). Die Leiste bricht je nach Breite
  // um, deshalb gemessen statt fest eingetragen.
  const toolbarObserver = useRef<ResizeObserver | null>(null);
  const toolbarRef = useCallback((el: HTMLDivElement | null) => {
    toolbarObserver.current?.disconnect();
    toolbarObserver.current = null;
    if (!el?.parentElement) return;
    const host = el.parentElement;
    const apply = () => host.style.setProperty('--hv-schema-toolbar-h', `${el.offsetHeight}px`);
    apply();
    toolbarObserver.current = new ResizeObserver(apply);
    toolbarObserver.current.observe(el);
  }, []);
  const canEditSchema = sessionSignal.value.user?.permissions.includes('plants.schema.edit') ?? false;
  const canAdminPlant = sessionSignal.value.user?.permissions.includes('plants.admin') ?? false;
  const location = useLocation();
  // Schema-Bearbeiten-Modus: nur sichtbar mit canEditSchema. Toggle in der
  // Baum-Toolbar; wenn aktiv rendert jede Node/Leaf-Zeile einen Drag-Adorner
  // + Pencil-Rename und wird zum Drop-Target.
  const [editMode, setEditMode] = useState(false);
  const [selectedLeafIds, setSelectedLeafIds] = useState<Set<string>>(new Set());
  // Wenn edit-Mode ausgeschaltet wird, Selection wegwerfen — sonst hängt
  // ein invisibles State-Blob am Component und ein späterer Klick auf
  // "Auswahl aufheben" wäre unmöglich weil der Button nicht mehr sichtbar ist.
  useEffect(() => { if (!editMode) setSelectedLeafIds(new Set()); }, [editMode]);
  const editCtl: EditCtl = {
    canEdit: canEditSchema,
    editMode: canEditSchema && editMode,
    renameDisplay: async (kind, entityId, newName) => {
      const trimmed = newName.trim();
      const url = kind === 'node'
        ? `/api/plants/${id}/nodes/${entityId}`
        : `/api/plants/${id}/leaves/${entityId}`;
      try {
        await api.patch<unknown>(url, {
          displayName: trimmed.length === 0 ? null : trimmed,
          clearFields: trimmed.length === 0 ? ['DisplayName'] : undefined,
        });
        // Frisch nachladen — der DisplayName steckt in mehreren Baum-Ebenen
        // und ein optimistisches Patch ist mehr Kondom als nötig für diese
        // low-frequency Aktion.
        setSchemaOverride(null);
        setRefreshTick((t) => t + 1);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unbekannter Fehler';
        showToast({ tone: 'error', title: 'Umbenennen fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
      }
    },
    deleteEntity: async (kind, entityId, label) => {
      const what = kind === 'node' ? 'Node inkl. aller Unter-Nodes und Leaves' : 'Leaf';
      const ok = await showConfirm({
        title: `${what} löschen?`,
        message: `"${label}" wird unwiderruflich entfernt.`,
        confirmLabel: 'Löschen',
        danger: true,
      });
      if (!ok) return;
      const url = kind === 'node'
        ? `/api/plants/${id}/nodes/${entityId}`
        : `/api/plants/${id}/leaves/${entityId}`;
      try {
        await api.delete(url);
        setSchemaOverride(null);
        setRefreshTick((t) => t + 1);
      } catch (e) {
        const msg = e instanceof ApiError
          ? (e.status === 403 ? 'Keine Berechtigung.' : `Fehler ${e.status}.`)
          : 'Unbekannter Fehler.';
        showToast({ tone: 'error', title: 'Löschen fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
      }
    },
    isLeafSelected: (leafId) => selectedLeafIds.has(leafId),
    toggleLeafSelected: (leafId) => {
      setSelectedLeafIds((prev) => {
        const next = new Set(prev);
        if (next.has(leafId)) next.delete(leafId);
        else next.add(leafId);
        return next;
      });
    },
    selectedCount: selectedLeafIds.size,
    clearSelection: () => setSelectedLeafIds(new Set()),
    bulkDeleteSelected: async () => {
      if (selectedLeafIds.size === 0) return;
      const ok = await showConfirm({
        title: `${selectedLeafIds.size} Leaves löschen?`,
        message: 'Ausgewählte Datenpunkte werden unwiderruflich entfernt.',
        confirmLabel: 'Löschen',
        danger: true,
      });
      if (!ok) return;
      const ids = Array.from(selectedLeafIds);
      // Parallel-DELETEs. Bei 100+ Leaves throttle nötig, aber im
      // Praxis-Fall sind das eher 20-30 pro Klick.
      const results = await Promise.allSettled(
        ids.map((leafId) => api.delete(`/api/plants/${id}/leaves/${leafId}`)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      setSelectedLeafIds(new Set());
      setSchemaOverride(null);
      setRefreshTick((t) => t + 1);
      if (failed === 0) {
        showToast({ tone: 'success', title: `${ids.length} Leaves gelöscht`,
          autoHideAfterMs: 4000 });
      } else {
        showToast({ tone: 'warn', title: `${ids.length - failed}/${ids.length} Leaves gelöscht`,
          message: `${failed} Fehler — Rest siehe DevTools-Netzwerk.`,
          autoHideAfterMs: 8000 });
      }
    },
    optimize: async (deviceId) => {
      try {
        const res = await api.post<{ leavesLifted: number; nodesDeleted: number }>(
          `/api/plants/${id}/devices/${deviceId}/schema/optimize`, {});
        setSchemaOverride(null);
        setRefreshTick((t) => t + 1);
        showToast({
          tone: 'success',
          title: 'Baum aufgeräumt',
          message: `${res.leavesLifted} Leaves hochgezogen, ${res.nodesDeleted} leere Nodes gelöscht.`,
          autoHideAfterMs: 5000,
        });
      } catch (e) {
        const msg = e instanceof ApiError
          ? (e.status === 403 ? 'Keine Berechtigung.' : `Fehler ${e.status}.`)
          : 'Unbekannter Fehler.';
        showToast({ tone: 'error', title: 'Aufräumen fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
      }
    },
    reparent: async (kind, entityId, targetParentId) => {
      const url = kind === 'node'
        ? `/api/plants/${id}/nodes/${entityId}`
        : `/api/plants/${id}/leaves/${entityId}`;
      const body = targetParentId === null && kind === 'node'
        ? { makeRoot: true }
        : { parentNodeId: targetParentId };
      try {
        await api.patch<unknown>(url, body);
        setSchemaOverride(null);
        setRefreshTick((t) => t + 1);
      } catch (e) {
        const msg = e instanceof ApiError ? (e.status === 400
          ? 'Ziel liegt im eigenen Subtree oder auf einem anderen Device.'
          : e.status === 403 ? 'Keine Berechtigung.' : `Fehler ${e.status}.`)
          : 'Unbekannter Fehler.';
        showToast({ tone: 'error', title: 'Verschieben fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
      }
    },
    openAddNode: (parent) => setAddNodeParent(parent),
    openAddLeaf: (parent) => setAddLeafParent(parent),
    openNodeEdit: (node) => setNodeEdit(node),
    openDeviceEdit: (device) => setDeviceEdit(device),
    openPublishTemplate: (device) => setPublishTemplateDevice(device),
    openApplyTemplate: (device) => setApplyTemplateDevice(device),
    openRecognition: (device) => setRecognitionDevice(device),
    openBindTelemetry: (device) => setBindTelemetryDevice(device),
    openTelemetrySetEditor: (device) => setTelemetrySetEditorDevice(device),
    canRegisterMap: canAdminPlant || canEditSchema,
    canOem: canUseRemoteService(sessionSignal.value.user?.permissions),
    openRegisterMap: (device) => setRegisterMapDevice(device),
  };

  /** Apply a partial schema patch (from the local Save flow or from a hub
   *  event on another session) to the in-place schema copy. */
  const applyLeafPatch = (patch: LeafPatch) => {
    setSchemaOverride((prev) => (prev ? patchSchemaLeaf(prev, patch) : prev));
  };

  // Listen for schema updates from other sessions.
  useEffect(() => {
    if (!id) return undefined;
    return onLeafSchemaUpdated((evt) => {
      if (evt.plantId !== id) return;
      applyLeafPatch(evt as LeafPatch);
    });
  }, [id]);

  // Sync-Status pro Device: hält Server-Version + zuletzt vom Client bestätigte
  // Version. Wenn beide gleich → "synced" (grün), sonst "syncing" (gelb).
  // Wird aus dem SignalR-Hub gespeist; initial synced (bis das erste
  // schemaChanged reinkommt).
  const [syncByDevice, setSyncByDevice] = useState<Record<string, { latest: number; acked: number }>>({});
  useEffect(() => {
    if (!id) return undefined;
    const unsubChanged = onSchemaChanged((evt) => {
      if (evt.plantId !== id) return;
      setSyncByDevice((prev) => {
        const cur = prev[evt.deviceId] ?? { latest: 0, acked: 0 };
        // Nur nach oben — SignalR-Reihenfolge könnte einen älteren Event nach
        // einem neueren zustellen (unwahrscheinlich, aber defensive).
        if (evt.schemaVersion <= cur.latest) return prev;
        return { ...prev, [evt.deviceId]: { latest: evt.schemaVersion, acked: cur.acked } };
      });
    });
    const unsubAcked = onSchemaAcked((evt) => {
      if (evt.plantId !== id) return;
      setSyncByDevice((prev) => {
        const cur = prev[evt.deviceId] ?? { latest: 0, acked: 0 };
        if (evt.schemaVersion <= cur.acked) return prev;
        const next = { ...prev, [evt.deviceId]: { latest: Math.max(cur.latest, evt.schemaVersion), acked: evt.schemaVersion } };
        // Toast NUR wenn wir vorher aus dem Sync raus waren — sonst spammt
        // jeder ACK die Bar bei stabilen Devices.
        if (cur.latest > cur.acked && evt.schemaVersion >= cur.latest) {
          showToast({ tone: 'success', title: 'Schema am Client synchronisiert',
            message: `${evt.clientId} hat Schema-Version v${evt.schemaVersion} installiert.`,
            autoHideAfterMs: 4000 });
        }
        return next;
      });
    });
    return () => { unsubChanged(); unsubAcked(); };
  }, [id]);

  // Snapshot hydration: fetch `/api/plants/{id}/values` once per plant view
  // and merge the result into signalStore. Empty slots stay empty and render
  // as spinners until the first live push. If the fetch fails we mark the
  // plant "hydrated" anyway so the UI stops spinning forever — the SignalR
  // stream will paint values as they arrive, same as before this feature.
  useEffect(() => {
    if (!id) return undefined;
    if (!tryClaimSnapshotFetch(id)) return undefined;
    // Nicht abbrechen, wenn man die Seite verlaesst: der Abruf fuellt den
    // gemeinsamen Speicher der Anlage, und der wird beim Zurueckkommen
    // gebraucht (Patrick, 16.10.2026: Reiterwechsel brach die Aktualisierung ab).
    (async () => {
      try {
        const res = await api.get<GetValuesSnapshotResponse>(`/api/plants/${id}/values`);
        hydratePlantSnapshot(id, res.values);
      } catch (err) {
        console.warn(`[schema] snapshot fetch failed for plant ${id}`, err);
        markPlantHydrated(id);
      } finally {
        releaseSnapshotFetch(id);
      }
    })();
    return undefined;
  }, [id]);

  // Aufgeklappt, wenn man auf die Seite kommt (Patrick, 16.10.2026).
  const [tree, setTree] = useState<TreeControl>({ generation: 0, allOpen: true });
  const expandAll = () => setTree((t) => ({ generation: t.generation + 1, allOpen: true }));
  const collapseAll = () => setTree((t) => ({ generation: t.generation + 1, allOpen: false }));

  // Filter — hides leaves whose symbol / displayName / unit don't match. Nodes
  // stay visible only when at least one descendant matches. Active filter
  // forces children open regardless of the caret state, so the results are
  // visible without another click. Empty string = no filter.
  const [filter, setFilter] = useState('');
  const filterLower = filter.trim().toLowerCase();

  // Controller-Filter: entweder alle Devices sichtbar (null) oder exakt einer.
  // Zweite Achse neben dem Text-Filter — Text sucht auf Symbol/Name/Einheit,
  // Controller-Wahl reduziert komplett auf den ausgewählten Feld-Client
  // (nützlich wenn eine Anlage mehrere Regler hat und der Techniker gezielt
  // in einem sucht). Sichtbarkeit vom Picker ist rein CSS-getrieben
  // (:focus-within), State lebt nur für die tatsächliche Auswahl.
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);

  // Persistent view toggles — rowLines/flash/unit/minMaxBar/sparkline.
  // Kept together in one hook so the localStorage blob is a single object.
  const [viewSettings, updateViewSetting] = useSchemaViewSettings();

  // Single modal at a time, tracked at the page level so the tree stays
  // stateless — a click on any leaf's Send button sets this to that leaf.
  const [commandLeaf, setCommandLeaf] = useState<LeafDto | null>(null);
  const modalCtl: CommandModalCtl = { openFor: (leaf) => setCommandLeaf(leaf) };
  const configCtl: LeafConfigCtl = {
    openFor: (leaf) => setConfigLeafId(leaf.id),
    canEdit: canEditSchema,
  };

  if (state.status === 'loading') return <AppShell title="Plant"><p class="muted">Loading schema…</p></AppShell>;
  if (state.status === 'error') return <AppShell title="Plant"><p class="muted">Failed to load schema.</p></AppShell>;

  // Schema-override wins over the freshly-fetched state so local edits (and
  // hub-broadcast edits from other sessions) show up immediately without
  // waiting for the next full refetch.
  const plant = schemaOverride ?? state.data;
  const configLeaf = configLeafId ? findLeafById(plant, configLeafId) : null;
  // For the "Verschieben nach…" selector: locate the leaf's device, then
  // flatten its node tree with depth so the modal can render an indented
  // dropdown. Also derive the leaf's current parent so we know whether the
  // operator actually changed anything.
  const moveCtx = configLeafId ? findLeafMoveContext(plant, configLeafId) : null;
  return (
    <AppShell title={plant.name}>
      {id && <PlantSubNav plantId={id} plantName={plant.name} />}
      <div
        class={
          'hv-schema' +
          (viewSettings.rowLines ? ' hv-schema--gridlines' : '') +
          (viewSettings.sparkline ? ' hv-schema--spark' : '') +
          (viewSettings.minMaxBar ? ' hv-schema--mmb' : '')
        }
      >
        <div class="hv-schema-toolbar" ref={toolbarRef}>
          <div class="hv-schema-toolbar__row">
            <FreshnessLegend />
            <div
              class={
                'hv-schema-toolbar__filter-group'
                + (deviceFilter !== null ? ' hv-schema-toolbar__filter-group--pinned' : '')
              }
            >
              <input
                type="search"
                class="hv-schema-toolbar__filter"
                placeholder="Datenpunkte filtern (Symbol, Name, Einheit)…"
                value={filter}
                onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
                aria-label="Datenpunkte filtern"
              />
              {plant.devices.length > 1 && (
                <div class="hv-schema-toolbar__device-picker" role="group" aria-label="Controller-Filter">
                  <button
                    type="button"
                    class={
                      'hv-schema-toolbar__device-chip'
                      + (deviceFilter === null ? ' hv-schema-toolbar__device-chip--active' : '')
                    }
                    onClick={() => setDeviceFilter(null)}
                    title="Alle Controller anzeigen"
                  >
                    Alle
                  </button>
                  {plant.devices.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      class={
                        'hv-schema-toolbar__device-chip'
                        + (deviceFilter === d.id ? ' hv-schema-toolbar__device-chip--active' : '')
                      }
                      onClick={() => setDeviceFilter(d.id)}
                      title={`${d.name ?? d.symbol}${d.protocol ? ' — ' + d.protocol : ''}`}
                    >
                      {d.name ?? d.symbol}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div class="hv-schema-toolbar__actions">
              <ToolGroup label="Baum">
                <ToolButton label="Alle ausklappen" onClick={expandAll}><IconExpandAll size={18} /></ToolButton>
                <ToolButton label="Alle einklappen" onClick={collapseAll}><IconCollapseAll size={18} /></ToolButton>
              </ToolGroup>
              <ToolGroup label="Anlage">
                {/* "Befehle" hat keinen eigenen Sub-Nav-Tab mehr, der Sprung in
                    die Kommando-Historie sitzt hier (User-Feedback 2026-08-28). */}
                <ToolButton label="Befehls-Historie: alle Schreibaktionen dieser Anlage, auch aus dem OEM-Modus"
                            href={`/plants/${id}/commands`}><IconHistory size={18} /></ToolButton>
                {canAdminPlant && (
                  <ToolButton label="Anlage bearbeiten" onClick={() => setShowPlantEdit(true)}><IconPlantSettings size={18} /></ToolButton>
                )}
                {canEditSchema && (
                  <ToolButton label={editMode ? 'Schema-Bearbeiten beenden' : 'Schema bearbeiten: Nodes und Leaves verschieben und umbenennen'}
                              active={editMode} onClick={() => setEditMode((v) => !v)}><IconSchemaEdit size={18} /></ToolButton>
                )}
              </ToolGroup>
              {editMode && selectedLeafIds.size > 0 && (
                <>
                  <span class="hv-schema-edit__selcount">
                    {selectedLeafIds.size} Leaf{selectedLeafIds.size === 1 ? '' : 's'} ausgewählt
                  </span>
                  <button type="button"
                          class="hv-tree-toolbar__btn"
                          onClick={() => setSelectedLeafIds(new Set())}>
                    Auswahl aufheben
                  </button>
                  <button type="button"
                          class="hv-tree-toolbar__btn hv-tree-toolbar__btn--danger"
                          onClick={() => void editCtl.bulkDeleteSelected()}>
                    🗑 Ausgewählte löschen
                  </button>
                </>
              )}
            </div>
          </div>
          <div class="hv-schema-toolbar__row hv-schema-toolbar__row--view">
            <span class="hv-schema-toolbar__view-label">Ansicht:</span>
            <ViewToggle
              settings={viewSettings}
              onChange={updateViewSetting}
              keyName="rowLines"
              label="Zeilenlinien"
            />
            <ViewToggle
              settings={viewSettings}
              onChange={updateViewSetting}
              keyName="flashOnChange"
              label="Blink bei Änderung"
            />
            <ViewToggle
              settings={viewSettings}
              onChange={updateViewSetting}
              keyName="showUnit"
              label="Einheit"
            />
            <ViewToggle
              settings={viewSettings}
              onChange={updateViewSetting}
              keyName="minMaxBar"
              label="Min/Max-Balken"
            />
            <ViewToggle
              settings={viewSettings}
              onChange={updateViewSetting}
              keyName="sparkline"
              label="Sparkline"
            />
          </div>
        </div>
        <div class="hv-schema-body stack-4">
          {plant.devices
            .filter((d) => deviceFilter === null || d.id === deviceFilter)
            .map((d) => (
            <DeviceCard
              key={d.id}
              plantId={id ?? ''}
              device={d}
              live={live}
              tree={tree}
              modalCtl={modalCtl}
              configCtl={configCtl}
              editCtl={editCtl}
              filter={filterLower}
              settings={viewSettings}
              syncState={syncByDevice[d.id]}
            />
          ))}
          {plant.devices.length === 0 && (
            <p class="muted">This plant has no devices yet.</p>
          )}
        </div>
      </div>
      {commandLeaf && id && (
        <SendCommandModal
          plantId={id}
          leaf={commandLeaf}
          onClose={() => setCommandLeaf(null)}
        />
      )}
      {configLeaf && id && (
        <LeafConfigModal
          plantId={id}
          leaf={configLeaf}
          moveOptions={moveCtx?.options}
          currentParentNodeId={moveCtx?.currentParentNodeId}
          onSaved={(patch) => {
            // Local save: patch our own schema immediately (before the hub
            // broadcast comes back). The onLeafSchemaUpdated listener will
            // no-op because the same fields are already patched in.
            applyLeafPatch(patch);
            setConfigLeafId(null);
          }}
          onDeleted={(leafId) => {
            // Soft-delete happened server-side. Strip the leaf from every
            // node it might belong to; only one match is expected.
            setSchemaOverride((prev) => (prev ? removeLeafFromSchema(prev, leafId) : prev));
            setConfigLeafId(null);
          }}
          onMoved={() => {
            // Reparent: full refetch, easier than surgical local move.
            setSchemaOverride(null);
            setRefreshTick((t) => t + 1);
          }}
          onClose={() => setConfigLeafId(null)}
        />
      )}
      {nodeEdit && id && (
        <NodeEditModal
          plantId={id}
          node={{
            id: nodeEdit.id,
            symbol: nodeEdit.symbol,
            displayName: nodeEdit.displayName ?? null,
            category: nodeEdit.category ?? null,
            sortOrder: nodeEdit.sortOrder,
          }}
          onSaved={(patch) => {
            setSchemaOverride((prev) => (prev ? patchNodeInSchema(prev, patch) : prev));
            setNodeEdit(null);
          }}
          onDeleted={(nodeId) => {
            setSchemaOverride((prev) => (prev ? removeNodeFromSchema(prev, nodeId) : prev));
            setNodeEdit(null);
          }}
          onClose={() => setNodeEdit(null)}
        />
      )}
      {addNodeParent && id && (
        <AddNodeModal
          plantId={id}
          parent={addNodeParent}
          onCreated={(node) => {
            setSchemaOverride((prev) => (prev ? insertNodeInSchema(prev, node) : prev));
            setAddNodeParent(null);
          }}
          onClose={() => setAddNodeParent(null)}
        />
      )}
      {addLeafParent && id && (
        <AddLeafModal
          plantId={id}
          parent={addLeafParent}
          onCreated={(leaf) => {
            setSchemaOverride((prev) => (prev ? insertLeafInSchema(prev, leaf) : prev));
            setAddLeafParent(null);
          }}
          onClose={() => setAddLeafParent(null)}
        />
      )}
      {deviceEdit && id && (
        <DeviceEditModal
          plantId={id}
          device={{
            id: deviceEdit.id,
            symbol: deviceEdit.symbol,
            name: deviceEdit.name,
            protocol: deviceEdit.protocol,
            firmware: deviceEdit.firmware,
            category: deviceEdit.category ?? null,
            categoryVariant: deviceEdit.categoryVariant ?? null,
            isPrimary: deviceEdit.isPrimary ?? false,
          }}
          onSaved={() => {
            // Server-side rename can shift Protocol too; simplest correct
            // refresh is a full refetch. Cheaper diffs are possible later.
            setDeviceEdit(null);
            setSchemaOverride(null);
            setRefreshTick((t) => t + 1);
          }}
          onDeleted={() => {
            setDeviceEdit(null);
            setSchemaOverride(null);
            setRefreshTick((t) => t + 1);
          }}
          onClose={() => setDeviceEdit(null)}
        />
      )}
      {publishTemplateDevice && id && (
        <PublishSchemaTemplateModal
          plantId={id}
          deviceId={publishTemplateDevice.id}
          deviceProtocol={publishTemplateDevice.protocol}
          onPublished={(templateId, version) => {
            showToast({
              tone: 'success',
              title: 'Template gespeichert',
              message: `Version v${version} liegt jetzt im Katalog. (Id: ${templateId.slice(0, 8)}…)`,
              autoHideAfterMs: 6000,
            });
          }}
          onClose={() => setPublishTemplateDevice(null)}
        />
      )}
      {registerMapDevice && (
        <RegisterMapModal
          deviceId={registerMapDevice.id}
          deviceName={registerMapDevice.name || registerMapDevice.symbol}
          protocol={registerMapDevice.protocol}
          onClose={() => setRegisterMapDevice(null)}
        />
      )}
      {recognitionDevice && id && (
        <RecognitionModal
          plantId={id}
          deviceId={recognitionDevice.id}
          deviceName={recognitionDevice.name}
          onApplied={() => setRefreshTick((t) => t + 1)}
          onClose={() => { setRecognitionDevice(null); setRefreshTick((t) => t + 1); }}
        />
      )}
      {applyTemplateDevice && id && (
        <ApplySchemaTemplateModal
          plantId={id}
          deviceId={applyTemplateDevice.id}
          deviceProtocol={applyTemplateDevice.protocol}
          isTargetEmpty={applyTemplateDevice.nodes.length === 0}
          onApplied={(result) => {
            showToast({
              tone: 'success',
              title: 'Template angewandt',
              message: `${result.nodesCreated} Nodes, ${result.leavesCreated} Leaves angelegt.`,
              autoHideAfterMs: 4000,
            });
            setApplyTemplateDevice(null);
            setRefreshTick((t) => t + 1);
          }}
          onClose={() => setApplyTemplateDevice(null)}
        />
      )}
      {bindTelemetryDevice && id && (
        <BindTelemetrySetModal
          plantId={id}
          deviceId={bindTelemetryDevice.id}
          deviceLabel={bindTelemetryDevice.name || bindTelemetryDevice.symbol}
          onApplied={(outcome: TelemetryBindingOutcome) => {
            // Die Warnungen gehören in den Toast, nicht nur in den Dialog:
            // §4.4 verlangt, dass nicht auflösbare Punkte angezeigt werden,
            // und der Dialog ist beim Erfolg schon zu. Der Ton wechselt
            // deshalb auf 'warning', wenn etwas offen blieb — ein grüner
            // Erfolgs-Toast über einem halb gebundenen Set wäre die stille
            // Variante, die wir gerade vermeiden wollen.
            const partial = outcome.unresolvedCount > 0;
            showToast({
              tone: partial ? 'warn' : 'success',
              title: partial ? 'Telemetrieset teilweise gebunden' : 'Telemetrieset gebunden',
              message: partial
                ? `${outcome.boundCount} von ${outcome.pointCount} Punkten gebunden, `
                  + `${outcome.unresolvedCount} nicht auflösbar. `
                  + 'Details im Telemetrie-Dialog des Reglers.'
                : `${outcome.boundCount} Datenpunkte werden aufgezeichnet `
                  + `(${outcome.recordedDatapointsAfter} von ${outcome.datapointLimit} erlaubten).`,
              autoHideAfterMs: partial ? 10000 : 5000,
            });
            setBindTelemetryDevice(null);
            // Ein Binding schreibt RecordingEnabled und die vier
            // Aufzeichnungsspalten auf die Leaves des Reglers. Ein Refetch
            // statt eines lokalen Patches, weil das Anwenden ein Ersetzen
            // ist — es räumt auch Leaves ab, die vorher aufgezeichnet
            // wurden, und die kennt die Antwort nicht einzeln.
            setSchemaOverride(null);
            setRefreshTick((t) => t + 1);
          }}
          onRemoved={() => {
            showToast({
              tone: 'success',
              title: 'Bindung entfernt',
              message: 'Der Regler zeichnet nichts mehr auf. Die Live-Anzeige läuft weiter.',
              autoHideAfterMs: 5000,
            });
            setBindTelemetryDevice(null);
            setSchemaOverride(null);
            setRefreshTick((t) => t + 1);
          }}
          onCreateSet={() => {
            // Aus dem Bind-Dialog in den Editor: derselbe Regler bleibt der
            // Kontext, damit „Aus Regler übernehmen" dort etwas anzubieten
            // hat.
            setTelemetrySetEditorDevice(bindTelemetryDevice);
            setBindTelemetryDevice(null);
          }}
          onClose={() => setBindTelemetryDevice(null)}
        />
      )}
      {telemetrySetEditorDevice && (
        <TelemetrySetEditorModal
          set={null}
          suggestFromNodes={telemetrySetEditorDevice.nodes}
          onSaved={(saved: TelemetrySetDetail) => {
            showToast({
              tone: 'success',
              title: 'Telemetrieset gespeichert',
              message: `„${saved.name}" v${saved.version} mit ${saved.points.length} Punkt(en). `
                + 'Es wirkt erst, wenn es auf einem Regler gebunden wird.',
              autoHideAfterMs: 7000,
            });
            // Zurück in den Bind-Dialog: ein Set anzulegen ist fast immer
            // der Vorlauf zum Binden, und der Operator soll dafür nicht
            // erneut suchen müssen.
            const device = telemetrySetEditorDevice;
            setTelemetrySetEditorDevice(null);
            setBindTelemetryDevice(device);
          }}
          onClose={() => setTelemetrySetEditorDevice(null)}
        />
      )}
      {showPlantEdit && id && (
        <PlantMetadataModal
          plant={{
            id,
            displayName: plant.name,
            latitude: plant.latitude ?? null,
            longitude: plant.longitude ?? null,
            address: plant.address ?? null,
          }}
          onSaved={(patched) => {
            // Patch the header title in place. A full refetch would pick up
            // the same fields, but the modal-close roundtrip already carries
            // the truth so we just apply it.
            setSchemaOverride((prev) =>
              prev
                ? { ...prev, name: patched.displayName, address: patched.address ?? null,
                    latitude: patched.latitude ?? null, longitude: patched.longitude ?? null }
                : prev,
            );
            setShowPlantEdit(false);
          }}
          onDeleted={() => {
            setShowPlantEdit(false);
            location.route('/plants');
          }}
          onClose={() => setShowPlantEdit(false)}
        />
      )}
    </AppShell>
  );
}

/** Rebuild the schema so a Node's patchable metadata is fresh in place. */
function patchNodeInSchema(plant: PlantSchemaResponse, patch: NodePatch): PlantSchemaResponse {
  return {
    ...plant,
    devices: plant.devices.map((d) => ({ ...d, nodes: patchNodes(d.nodes, patch) })),
  };
}
function patchNodes(nodes: NodeDto[], patch: NodePatch): NodeDto[] {
  return nodes.map((n) => {
    if (n.id === patch.id) {
      return { ...n, displayName: patch.displayName, category: patch.category, sortOrder: patch.sortOrder };
    }
    return { ...n, children: patchNodes(n.children, patch) };
  });
}

/** Drop a Node (soft-deleted server-side) and its subtree from the local
 *  schema copy so the tree reflects the change without a refetch. */
function removeNodeFromSchema(plant: PlantSchemaResponse, nodeId: string): PlantSchemaResponse {
  return {
    ...plant,
    devices: plant.devices.map((d) => ({ ...d, nodes: dropNode(d.nodes, nodeId) })),
  };
}
function dropNode(nodes: NodeDto[], nodeId: string): NodeDto[] {
  return nodes
    .filter((n) => n.id !== nodeId)
    .map((n) => ({ ...n, children: dropNode(n.children, nodeId) }));
}

/** Insert a freshly-created Node into the correct branch of the schema. */
function insertNodeInSchema(plant: PlantSchemaResponse, node: AddedNode): PlantSchemaResponse {
  const fresh: NodeDto = {
    id: node.id,
    symbol: node.symbol,
    displayName: node.displayName,
    origin: 'Virtual',
    category: node.category,
    sortOrder: node.sortOrder,
    attributes: null,
    typicalDefinitionId: null,
    typicalDefinitionVersion: null,
    children: [],
    leaves: [],
  };
  return {
    ...plant,
    devices: plant.devices.map((d) => {
      if (d.id !== node.deviceId) return d;
      if (node.parentNodeId === null) return { ...d, nodes: [...d.nodes, fresh] };
      return { ...d, nodes: attachNode(d.nodes, node.parentNodeId, fresh) };
    }),
  };
}
function attachNode(nodes: NodeDto[], parentId: string, fresh: NodeDto): NodeDto[] {
  return nodes.map((n) => {
    if (n.id === parentId) return { ...n, children: [...n.children, fresh] };
    return { ...n, children: attachNode(n.children, parentId, fresh) };
  });
}

/** Insert a freshly-created Leaf under the correct parent node. */
function insertLeafInSchema(plant: PlantSchemaResponse, leaf: AddedLeaf): PlantSchemaResponse {
  const fresh: LeafDto = {
    id: leaf.id,
    symbol: leaf.symbol,
    displayName: leaf.displayName,
    valueType: leaf.valueType,
    metricKind: leaf.metricKind,
    unit: leaf.unit,
    dimension: leaf.dimension,
    minValue: leaf.minValue,
    maxValue: leaf.maxValue,
    decimals: leaf.decimals,
    maxLength: null,
    enumEntries: null,
    flagBits: null,
    writability: leaf.writability,
    readSensitivity: leaf.readSensitivity,
    writeSensitivity: leaf.writeSensitivity,
    safetyMarker: leaf.safetyMarker,
    warnLow: leaf.warnLow,
    warnHigh: leaf.warnHigh,
    alarmLow: leaf.alarmLow,
    alarmHigh: leaf.alarmHigh,
    escalateToAlarm: leaf.escalateToAlarm,
    forceAllowed: leaf.forceAllowed,
    attributes: null,
    sortOrder: leaf.sortOrder,
    // Binding fields start null on freshly-created leaves; the operator
    // sets them via the same modal (Akquisition-Section) later, or
    // Discovery-Commit populates them.
    address: null,
    scale: null,
    offset: null,
    pollIntervalSeconds: null,
    heartbeatSeconds: null,
    deadband: null,
    covPercent: null,
    minIntervalSeconds: null,
    // Ein frisches Leaf wird nicht aufgezeichnet (J.20: Default false). Der
    // Weg dorthin ist ein Telemetrieset, das den Punkt aufnimmt.
    recordingEnabled: false,
  };
  return {
    ...plant,
    devices: plant.devices.map((d) => ({ ...d, nodes: attachLeaf(d.nodes, leaf.parentNodeId, fresh) })),
  };
}
function attachLeaf(nodes: NodeDto[], parentId: string, fresh: LeafDto): NodeDto[] {
  return nodes.map((n) => {
    if (n.id === parentId) return { ...n, leaves: [...n.leaves, fresh] };
    return { ...n, children: attachLeaf(n.children, parentId, fresh) };
  });
}

/** Remove a leaf (soft-deleted server-side) from the local schema copy so
 *  it disappears from the tree without a full refetch. */
function removeLeafFromSchema(plant: PlantSchemaResponse, leafId: string): PlantSchemaResponse {
  return {
    ...plant,
    devices: plant.devices.map((d) => ({ ...d, nodes: stripLeafFromNodes(d.nodes, leafId) })),
  };
}
function stripLeafFromNodes(nodes: NodeDto[], leafId: string): NodeDto[] {
  return nodes.map((n) => ({
    ...n,
    leaves: n.leaves.filter((l) => l.id !== leafId),
    children: stripLeafFromNodes(n.children, leafId),
  }));
}

/** Locate a leaf by id in a plant schema. Returns null if not found. */
function findLeafById(plant: PlantSchemaResponse, leafId: string): LeafDto | null {
  for (const device of plant.devices) {
    const hit = findInNodes(device.nodes, leafId);
    if (hit) return hit;
  }
  return null;
}

/**
 * Locate a leaf, then produce the "Verschieben nach…"-Kontext: the current
 * parent-node id + a flat depth-annotated list of every node under the
 * leaf's device. The modal renders this as an indented dropdown.
 */
function findLeafMoveContext(
  plant: PlantSchemaResponse,
  leafId: string,
): { currentParentNodeId: string; options: MoveOption[] } | null {
  for (const device of plant.devices) {
    const parent = findLeafParent(device.nodes, leafId);
    if (parent === null) continue;
    return { currentParentNodeId: parent, options: flattenNodesForMove(device.nodes, 0) };
  }
  return null;
}
function findLeafParent(nodes: NodeDto[], leafId: string): string | null {
  for (const node of nodes) {
    if (node.leaves.some((l) => l.id === leafId)) return node.id;
    const nested = findLeafParent(node.children, leafId);
    if (nested !== null) return nested;
  }
  return null;
}
function flattenNodesForMove(nodes: NodeDto[], depth: number): MoveOption[] {
  const out: MoveOption[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, label: node.displayName ?? node.symbol, depth });
    out.push(...flattenNodesForMove(node.children, depth + 1));
  }
  return out;
}
function findInNodes(nodes: NodeDto[], leafId: string): LeafDto | null {
  for (const node of nodes) {
    for (const leaf of node.leaves) {
      if (leaf.id === leafId) return leaf;
    }
    const nested = findInNodes(node.children, leafId);
    if (nested) return nested;
  }
  return null;
}

/** Patch a single leaf inside a PlantSchemaResponse. Returns a new
 *  response with the patched leaf at the same position; unrelated devices
 *  and nodes share their reference so Preact can skip re-renders. */
function patchSchemaLeaf(plant: PlantSchemaResponse, patch: LeafPatch): PlantSchemaResponse {
  const patchLeaf = (leaf: LeafDto): LeafDto => ({
    ...leaf,
    displayName: patch.displayName,
    unit: patch.unit,
    dimension: patch.dimension,
    minValue: patch.minValue,
    maxValue: patch.maxValue,
    decimals: patch.decimals,
    warnLow: patch.warnLow,
    warnHigh: patch.warnHigh,
    alarmLow: patch.alarmLow,
    alarmHigh: patch.alarmHigh,
    readSensitivity: patch.readSensitivity,
    writeSensitivity: patch.writeSensitivity,
    safetyMarker: patch.safetyMarker,
    forceAllowed: patch.forceAllowed,
    escalateToAlarm: patch.escalateToAlarm,
    metricKind: patch.metricKind,
    // Binding fields: optional on LeafPatch. undefined = untouched (keep
    // whatever the leaf already carries). Explicit null = server cleared
    // it, mirror that.
    address: patch.address === undefined ? leaf.address : patch.address,
    scale: patch.scale === undefined ? leaf.scale : patch.scale,
    offset: patch.offset === undefined ? leaf.offset : patch.offset,
    pollIntervalSeconds: patch.pollIntervalSeconds === undefined ? leaf.pollIntervalSeconds : patch.pollIntervalSeconds,
    heartbeatSeconds: patch.heartbeatSeconds === undefined ? leaf.heartbeatSeconds : patch.heartbeatSeconds,
    deadband: patch.deadband === undefined ? leaf.deadband : patch.deadband,
    covPercent: patch.covPercent === undefined ? leaf.covPercent : patch.covPercent,
    minIntervalSeconds: patch.minIntervalSeconds === undefined
      ? leaf.minIntervalSeconds
      : patch.minIntervalSeconds,
    // recordingEnabled steht bewusst nicht im LeafPatch: das Bit gehoert dem
    // Telemetrieset-Binding, nicht dem Leaf-Editor. Es aendert sich nur beim
    // Anwenden oder Entfernen eines Sets, und beides loest hier einen
    // Refetch aus.
  });
  const walkNodes = (nodes: NodeDto[]): NodeDto[] => nodes.map((n) => ({
    ...n,
    leaves: n.leaves.map((l) => (l.id === patch.leafId ? patchLeaf(l) : l)),
    children: walkNodes(n.children),
  }));
  return {
    ...plant,
    devices: plant.devices.map((d) => ({
      ...d,
      nodes: walkNodes(d.nodes),
    })),
  };
}

/** Tiny label+checkbox pair for the view-settings toolbar row. Extracted
 *  so the toolbar JSX stays scannable. */
function ViewToggle<K extends keyof SchemaViewSettings>({
  settings,
  onChange,
  keyName,
  label,
}: {
  settings: SchemaViewSettings;
  onChange: <T extends keyof SchemaViewSettings>(k: T, v: SchemaViewSettings[T]) => void;
  keyName: K;
  label: string;
}) {
  return (
    <label class="hv-schema-toolbar__toggle">
      <input
        type="checkbox"
        checked={settings[keyName] as unknown as boolean}
        onChange={(e) =>
          onChange(keyName, (e.currentTarget as HTMLInputElement).checked as SchemaViewSettings[K])
        }
      />
      {label}
    </label>
  );
}

/** True when this node's own symbol/displayName/category matches the filter.
 *  Independent of children — used both for "should the node itself surface as
 *  a match" and for "if I match, show all my subtree unconditionally". */
function nodeSelfMatches(node: NodeDto, filter: string): boolean {
  return node.symbol.toLowerCase().includes(filter)
      || (node.displayName?.toLowerCase().includes(filter) ?? false)
      || (node.category?.toLowerCase().includes(filter) ?? false);
}

/** True when this node itself matches, or ANY of its transitive descendants
 *  (nodes or leaves) matches. Determines whether the node is visible under
 *  an active filter. */
function nodeMatchesFilter(node: NodeDto, filter: string): boolean {
  if (!filter) return true;
  if (nodeSelfMatches(node, filter)) return true;
  if (node.leaves.some((l) => leafMatchesFilter(l, filter))) return true;
  return node.children.some((c) => nodeMatchesFilter(c, filter));
}

function leafMatchesFilter(leaf: LeafDto, filter: string): boolean {
  if (!filter) return true;
  return leaf.symbol.toLowerCase().includes(filter)
      || (leaf.displayName?.toLowerCase().includes(filter) ?? false)
      || (leaf.unit?.toLowerCase().includes(filter) ?? false);
}

/** Collect every leaf id underneath a set of nodes (recursive). */
function collectLeafIds(nodes: NodeDto[]): string[] {
  const out: string[] = [];
  const walk = (arr: NodeDto[]) => {
    for (const n of arr) {
      for (const l of n.leaves) out.push(l.id);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** RX-LED hook: attach the returned callback ref to a wrapper element.
 *  Whenever any live-value signal under `nodes` updates, the wrapper gets a
 *  brief `.hv-device__rx--pulse` class so the leftmost edge of the badge
 *  flashes accent-green. Uses signal.subscribe() directly so the DeviceCard
 *  itself never re-renders on the pulse. */
function useDeviceRxLed(live: PlantLiveState | null, nodes: NodeDto[]): (el: HTMLElement | null) => void {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const leafIds = useMemo(() => collectLeafIds(nodes), [nodes]);

  useEffect(() => {
    if (!live || !target || leafIds.length === 0) return;
    let initialSweep = true;
    let clearTimer: number | null = null;
    const trigger = () => {
      target.classList.remove('hv-device__rx--pulse');
      // Force reflow so a rapid second update actually restarts the animation.
      void target.offsetWidth;
      target.classList.add('hv-device__rx--pulse');
      if (clearTimer !== null) window.clearTimeout(clearTimer);
      clearTimer = window.setTimeout(() => {
        target.classList.remove('hv-device__rx--pulse');
      }, 550);
    };
    const unsubs = leafIds.map((id) =>
      live.leafSignal(id).subscribe(() => {
        // Signals fire once immediately on subscribe; skip those initial calls
        // so mounting the card doesn't blink the LED before any traffic came in.
        if (initialSweep) return;
        trigger();
      }),
    );
    // Every subscribe above ran synchronously; anything from here on is a
    // genuine update.
    initialSweep = false;
    return () => {
      unsubs.forEach((u) => u());
      if (clearTimer !== null) window.clearTimeout(clearTimer);
    };
  }, [live, target, leafIds]);

  return setTarget;
}

/** RX-traffic strip: transparent 200 px band that spawns a fresh tick at
 *  its right edge on every live-value update; each tick then slides left
 *  and fades out. Rendered as a plain div plus imperatively-appended child
 *  spans so DeviceCard never re-renders. */
function DeviceRxStrip({ live, nodes }: { live: PlantLiveState | null; nodes: NodeDto[] }) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const leafIds = useMemo(() => collectLeafIds(nodes), [nodes]);

  useEffect(() => {
    if (!live || !container || leafIds.length === 0) return;
    let initialSweep = true;
    const appendTick = () => {
      const tick = document.createElement('span');
      tick.className = 'hv-rx-strip__tick';
      // Slight per-tick randomness on the vertical position gives the strip
      // a more "signal-noise" texture instead of a boring straight line.
      tick.style.top = `${3 + Math.random() * 2}px`;
      tick.style.bottom = `${3 + Math.random() * 2}px`;
      tick.addEventListener('animationend', () => tick.remove(), { once: true });
      container.appendChild(tick);
    };
    const unsubs = leafIds.map((id) =>
      live.leafSignal(id).subscribe(() => {
        if (initialSweep) return;
        appendTick();
      }),
    );
    initialSweep = false;
    return () => {
      unsubs.forEach((u) => u());
      while (container.firstChild) container.removeChild(container.firstChild);
    };
  }, [live, container, leafIds]);

  return <div class="hv-rx-strip" ref={setContainer} aria-hidden="true" />;
}

function DeviceCard({
  plantId, device, live, tree, modalCtl, configCtl, editCtl, filter, settings, syncState,
}: { plantId: string; device: DeviceDto; live: PlantLiveState | null; tree: TreeControl; modalCtl: CommandModalCtl; configCtl: LeafConfigCtl; editCtl: EditCtl; filter: string; settings: SchemaViewSettings; syncState?: { latest: number; acked: number } }) {
  const attachRxLed = useDeviceRxLed(live, device.nodes);
  // OEM-Modus: statt des Betriebsschemas alle Punkte des Reglers, Werte ueber
  // den Fernzugang (Patrick, 22.10.2026). Je Geraet, nicht gespeichert.
  const [oem, setOem] = useState(false);
  const [oemProgress, setOemProgress] = useState<{ progress: { done: number; total: number }; stop: () => void } | null>(null);
  // Whole device gets hidden if the filter is active and NOTHING inside it
  // matches — otherwise it's visual noise. Im OEM-Modus sucht der Filter im
  // Katalog des Reglers, dort bleibt die Karte stehen.
  if (filter && !oem && !device.nodes.some((n) => nodeMatchesFilter(n, filter))) return null;
  return (
    <Card padded={false} class={oem ? 'hv-device--oem' : ''}>
      <CardHeader>
        <div class="hv-device__title-block grow" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0, gap: 2 }}>
          <span class="hv-device__name">{device.name}</span>
          <span class="hv-device__schema-origin"
                style={{
                  fontSize: '0.78rem',
                  color: 'var(--text-secondary, #6b7280)',
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}
                title={device.appliedTemplateName
                  ? `Schema-Template angewandt: ${device.appliedTemplateName} v${device.appliedTemplateVersion ?? '?'}`
                  : 'Kein Template angewandt — Baum stammt aus Discovery oder ist handgebaut'}>
            {oem
              ? <span style={{ color: 'var(--status-warn)' }}>OEM-Datenpunkte: alle Punkte des Reglers, nicht das Betriebsschema</span>
              : device.appliedTemplateName
              ? <>
                  <span style={{ opacity: 0.7, marginRight: '0.35em' }}>Schema:</span>
                  <span style={{ color: 'var(--text-primary, inherit)' }}>{device.appliedTemplateName}</span>
                  {device.appliedTemplateVersion !== null && device.appliedTemplateVersion !== undefined && (
                    <span style={{ marginLeft: '0.35em', opacity: 0.7 }}>v{device.appliedTemplateVersion}</span>
                  )}
                </>
              : <>
                  <span style={{ opacity: 0.7, marginRight: '0.35em' }}>Schema:</span>
                  <span style={{ fontStyle: 'italic' }}>Discovered Scheme</span>
                </>}
          </span>
        </div>
        {oem && oemProgress && (
          <span class="hv-oem__progress" role="status">
            <span class="hv-leaf__spinner" aria-hidden="true" />
            Lese {oemProgress.progress.done} von {oemProgress.progress.total}
            <button type="button" class="hv-btn hv-btn--sm" onClick={oemProgress.stop}>Anhalten</button>
          </span>
        )}
        <DeviceRxStrip live={live} nodes={device.nodes} />
        {device.protocol && (
          <span class="hv-device__rx" ref={attachRxLed} title="RX-LED: pulst kurz grün bei jedem eingehenden Wert">
            <StatusBadge tone="neutral">{device.protocol}</StatusBadge>
          </span>
        )}

        {device.commandCapable && <StatusBadge tone="info" title="Accepts commands">Command</StatusBadge>}
        {device.forceCapable && <StatusBadge tone="warn" title="Native force capability">Force</StatusBadge>}
        {syncState && (
          syncState.acked >= syncState.latest
            ? <StatusBadge tone="ok" title={`Field-Client hat Schema v${syncState.acked} installiert.`}>synced</StatusBadge>
            : <StatusBadge tone="warn" title={`Server v${syncState.latest}, Client v${syncState.acked} — Sync läuft (max. 60 s).`}>syncing…</StatusBadge>
        )}
        {editCtl.canEdit && !oem && (
          <ToolGroup label="Gerät">
            <ToolButton label="Gerät bearbeiten: umbenennen, Protokoll ändern oder löschen" onClick={() => editCtl.openDeviceEdit(device)}>
              <IconEdit size={18} />
            </ToolButton>
            <ToolButton label="Neuen Root-Node anlegen"
                        onClick={() => editCtl.openAddNode({ deviceId: device.id, parentNodeId: null, parentLabel: `Device: ${device.name}` })}>
              <IconNodeAdd size={18} />
            </ToolButton>
            {editCtl.editMode && (
              <ToolButton label="Baum aufräumen: alleinstehende Leaves hochziehen, leere Nodes löschen" onClick={() => editCtl.optimize(device.id)}>
                <IconCleanup size={18} />
              </ToolButton>
            )}
          </ToolGroup>
        )}
        {editCtl.canEdit && !oem && (
          <ToolGroup label="Schema">
            <ToolButton label="Template anwenden: ein publiziertes Schema-Template auf dieses Gerät" onClick={() => editCtl.openApplyTemplate(device)}>
              <IconTemplateApply size={18} />
            </ToolButton>
            <ToolButton label="Anlage erkennen: Konfiguration des Reglers lesen und das passende Schema vorschlagen" onClick={() => editCtl.openRecognition(device)}>
              <IconDiscovery size={18} />
            </ToolButton>
            <ToolButton label="Als Template speichern: Nodes und Leaves als wiederverwendbare Vorlage" onClick={() => editCtl.openPublishTemplate(device)}>
              <IconTemplateSave size={18} />
            </ToolButton>
            <ToolButton label="Telemetrie: festlegen, welche Datenpunkte dieser Regler aufzeichnet" onClick={() => editCtl.openBindTelemetry(device)}>
              <IconTelemetry size={18} />
            </ToolButton>
          </ToolGroup>
        )}
        {(editCtl.canRegisterMap || editCtl.canOem) && (
          <ToolGroup label="Servicezugang">
            {editCtl.canRegisterMap && (
              <ToolButton label="Registertabelle: der volle Adressraum für den Servicezugang, unabhängig vom Betriebsschema"
                          onClick={() => editCtl.openRegisterMap(device)}>
                <IconTable size={18} />
              </ToolButton>
            )}
            {editCtl.canOem && (
              <ToolButton label={oem ? 'Zurück zum Betriebsschema' : 'OEM-Datenpunkte: alle Punkte des Reglers über den Fernzugang'}
                          tone="accent" active={oem} onClick={() => setOem((v) => !v)}>
                <IconOem size={18} />
              </ToolButton>
            )}
          </ToolGroup>
        )}
      </CardHeader>
      <CardBody>
        <div class="stack-2">
          {syncState && syncState.latest > syncState.acked && (
            <div class="hv-device__sync-banner" role="status">
              <span class="hv-device__sync-anim" aria-hidden="true"
                    style={{ color: 'var(--status-warn)' }}>
                <SyncSpinner size={22} />
              </span>
              <div class="hv-device__sync-copy">
                <strong>Field-Client synchronisiert…</strong>
                <div class="hv-device__sync-note">
                  Schema v{syncState.latest} veröffentlicht. Der Client übernimmt die neue
                  OperationalMap in wenigen Sekunden — dann fließen die Werte auf den frischen Leaves.
                </div>
              </div>
            </div>
          )}
          {oem && (
            <OemDeviceTree plantId={plantId} deviceId={device.id} filter={filter}
                           onProgress={(progress, stop) => setOemProgress(progress ? { progress, stop } : null)} />
          )}
          {!oem && editCtl.editMode && (
            <DeviceRootDropZone deviceId={device.id} editCtl={editCtl} />
          )}
          {!oem && device.nodes.map((n) => (
            <NodeRow key={n.id} node={n} deviceId={device.id} depth={0} live={live} tree={tree} modalCtl={modalCtl}
              configCtl={configCtl} editCtl={editCtl} filter={filter} settings={settings} />
          ))}
          {!oem && device.nodes.length === 0 && (
            <p class="muted">Noch keine Nodes an diesem Device.</p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

interface NodeRowProps {
  node: NodeDto;
  /** DeviceId of the root device this node belongs to; passed down because
   *  NodeDto itself doesn't carry the denormalised DeviceId. */
  deviceId: string;
  depth: number;
  live: PlantLiveState | null;
  tree: TreeControl;
  modalCtl: CommandModalCtl;
  configCtl: LeafConfigCtl;
  editCtl: EditCtl;
  filter: string;
  settings: SchemaViewSettings;
  /** True when an ancestor node self-matched the filter — every descendant
   *  (nodes + leaves) is unconditionally visible. Propagates down so a
   *  matched node "opens up" its full subtree without each leaf having to
   *  match the filter itself. */
  forceShow?: boolean;
}

/** Recursive node row. Open one level by default; caret toggles below that.
 *  Expand/collapse-all bumps `tree.generation`; the useEffect below resets
 *  local open-state on every generation change. An active filter force-opens
 *  the node so matches are visible without another click. */
function NodeRow({ node, deviceId, depth, live, tree, modalCtl, configCtl, editCtl, filter, settings, forceShow }: NodeRowProps) {
  // Offen ab dem ersten Anzeigen, wie der Baum-Schalter steht (Vorgabe: alles offen).
  const [open, setOpen] = useState(tree.allOpen || depth < 1);
  useEffect(() => {
    setOpen(tree.allOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree.generation]);
  const [renaming, setRenaming] = useState(false);
  // Drop-Highlight NICHT über State — dragover feuert 60x/s und jeder
  // setState hier triggerte Preact-Reconciliation für den kompletten
  // Subtree (auf einer Node mit 100 Kindern: Lag-Katastrophe). Wir setzen
  // die CSS-Klasse direkt am DOM-Element via Ref.
  const headerRef = useRef<HTMLDivElement | null>(null);
  const setDropVisual = (active: boolean) => {
    const el = headerRef.current;
    if (!el) return;
    if (active) el.classList.add('hv-node--drop-target');
    else el.classList.remove('hv-node--drop-target');
  };

  // Hide entire node subtree if nothing under it matches the filter — unless
  // an ancestor's forceShow is telling us to render anyway.
  if (filter && !forceShow && !nodeMatchesFilter(node, filter)) return null;

  const hasChildren = node.children.length + node.leaves.length > 0;
  const showChildren = (open || filter.length > 0 || forceShow) && hasChildren;
  // If this node itself matched the filter, unconditionally show every
  // descendant — the operator asked for "kessel", so the whole "kessel"
  // subtree should be readable, not just leaves whose names happen to match.
  const childForceShow = forceShow || (filter.length > 0 && nodeSelfMatches(node, filter));

  // Drop-Handler auf einer Node-Row: das Ziel wird als neuer Parent für
  // den gezogenen Node/Leaf gesetzt. Bei collapsed-Node öffnen wir ihn
  // sofort damit der Operator sehen kann was gerade eingehängt wurde.
  const onDragOver = (e: DragEvent) => {
    if (!editCtl.editMode) return;
    if (!e.dataTransfer?.types.includes('application/x-hvacloud-schema')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropVisual(true);
    // setOpen ist State und triggert einen Render — nur feuern wenn nötig.
    if (hasChildren && !open) setOpen(true);
  };
  const onDragLeave = (e: DragEvent) => {
    if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) return;
    setDropVisual(false);
  };
  const onDrop = (e: DragEvent) => {
    if (!editCtl.editMode) return;
    setDropVisual(false);
    const payload = e.dataTransfer?.getData('application/x-hvacloud-schema');
    if (!payload) return;
    e.preventDefault();
    try {
      const { kind, id } = JSON.parse(payload) as { kind: 'node' | 'leaf'; id: string };
      if (kind === 'node' && id === node.id) return; // no-op on self
      void editCtl.reparent(kind, id, node.id);
    } catch { /* payload malformed — ignore */ }
  };
  const onDragStart = (e: DragEvent) => {
    if (!editCtl.editMode || !e.dataTransfer) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-hvacloud-schema',
      JSON.stringify({ kind: 'node', id: node.id }));
  };

  return (
    <div class="hv-node" style={{ paddingLeft: `${depth * 10}px` }}>
      {/* Header-Row als eigenes Element mit den Drop-Handlern. Der
        * Kinder-Container liegt ausserhalb dieses Divs, sodass ein Drop
        * auf eine tief geschachtelte Zeile nicht an diesen Ancestor
        * hochpropagiert — das war die Ursache der "Leaf fliegt raus"-
        * Erfahrung. `nowrap` verhindert dass die Icons bei zusätzlichen
        * Adornern in die nächste Zeile brechen. */}
      <div class="hv-node__header"
           ref={headerRef}
           onDragOver={onDragOver}
           onDragLeave={onDragLeave}
           onDrop={onDrop}>
      {editCtl.editMode && (
        <span class="hv-schema-edit__handle"
              title="Ziehen zum Verschieben"
              draggable
              onDragStart={onDragStart}>⋮⋮</span>
      )}
      <button class="hv-node__caret" onClick={() => setOpen((o) => !o)} disabled={!hasChildren}>
        {hasChildren ? (showChildren ? '▾' : '▸') : '·'}
      </button>
      {renaming ? (
        <InlineRename
          initial={node.displayName ?? node.symbol}
          onCommit={(v) => { setRenaming(false); void editCtl.renameDisplay('node', node.id, v); }}
          onCancel={() => setRenaming(false)} />
      ) : (
        <>
          {editCtl.editMode && (
            <button type="button"
                    class="hv-schema-edit__pencil"
                    title="Anzeigename bearbeiten"
                    onClick={() => setRenaming(true)}>✎</button>
          )}
          <span class="hv-node__name">{node.displayName ?? node.symbol}</span>
        </>
      )}
      {/* Im Edit-Mode nur die zwingend nötigen Adorner (Handle, Pencil, Trash).
        * Kategorie-Badge, virtual-Origin-Badge und die Add-Buttons blenden aus —
        * das minimiert Rendering und reduziert visuelle Ablenkung während der
        * Baum-Bearbeitung, wie vom Operator explizit gewünscht. */}
      {!editCtl.editMode && node.category && <StatusBadge tone="neutral">{node.category}</StatusBadge>}
      {/* origin === 'Virtual' Badge komplett entfernt — die Info war ohne
        * Erklärung nur verwirrend, und in der Realität ist praktisch jeder
        * discovery-erzeugte Node "Virtual". Origin bleibt im Datenmodell,
        * wird aber nirgends mehr angezeigt. */}
      {editCtl.canEdit && !editCtl.editMode && (
        <span class="hv-node__edit-slot">
          <button
            type="button"
            class="hv-node__edit-btn"
            title="Kind-Node anlegen"
            onClick={() => editCtl.openAddNode({ deviceId, parentNodeId: node.id, parentLabel: node.displayName ?? node.symbol })}
          >+ Node</button>
          <button
            type="button"
            class="hv-node__edit-btn"
            title="Leaf anlegen"
            onClick={() => editCtl.openAddLeaf({ parentNodeId: node.id, parentLabel: node.displayName ?? node.symbol })}
          >+ Leaf</button>
          <button
            type="button"
            class="hv-node__edit-btn"
            title="Node bearbeiten"
            onClick={() => editCtl.openNodeEdit(node)}
          >⚙</button>
        </span>
      )}
      {editCtl.editMode && (
        <button type="button"
                class="hv-schema-edit__trash"
                title="Node inkl. Sub-Tree löschen"
                onClick={() => editCtl.deleteEntity('node', node.id, node.displayName ?? node.symbol)}>🗑</button>
      )}
      </div>{/* ← end .hv-node__header */}
      {showChildren && (
        <div class="hv-node__children">
          {node.children.map((c) => (
            <NodeRow key={c.id} node={c} deviceId={deviceId} depth={depth + 1} live={live} tree={tree} modalCtl={modalCtl}
              configCtl={configCtl} editCtl={editCtl} filter={filter} settings={settings} forceShow={childForceShow} />
          ))}
          {node.leaves.map((l) => (
            <LeafRow key={l.id} leaf={l} depth={depth + 1} live={live} modalCtl={modalCtl}
              configCtl={configCtl} editCtl={editCtl} filter={filter} settings={settings} forceShow={childForceShow} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeafRow({
  leaf, depth, live, modalCtl, configCtl, editCtl, filter, settings, forceShow,
}: { leaf: LeafDto; depth: number; live: PlantLiveState | null; modalCtl: CommandModalCtl; configCtl: LeafConfigCtl; editCtl?: EditCtl; filter: string; settings: SchemaViewSettings; forceShow?: boolean }) {
  if (filter && !forceShow && !leafMatchesFilter(leaf, filter)) return null;

  // Im Schema-Bearbeiten-Modus rendert die Zeile massiv reduziert: nur
  // Handle, Marker, Pencil+Name, Trash. KEINE LeafValueDisplay, kein
  // Chart-Toggle, keine Badges, kein Sensitivity-Icon, kein Send-Button,
  // kein History-Panel. Das ist zwingend für die Perf — bei 5000 Leaves
  // erspart der Early-Return jeder Zeile Dutzende Wrapper-Nodes.
  if (editCtl?.editMode) {
    const selected = editCtl.isLeafSelected(leaf.id);
    return (
      <div class="hv-leaf hv-leaf--edit" style={{ paddingLeft: `${depth * 10}px` }}>
        <label class="hv-schema-edit__check" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox"
                 checked={selected}
                 onChange={() => editCtl.toggleLeafSelected(leaf.id)}
                 aria-label={`Leaf ${leaf.symbol} auswählen`} />
        </label>
        <span class="hv-schema-edit__handle hv-schema-edit__handle--leaf"
              title="Ziehen zum Verschieben"
              draggable
              onDragStart={(e) => {
                if (!e.dataTransfer) return;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('application/x-hvacloud-schema',
                  JSON.stringify({ kind: 'leaf', id: leaf.id }));
              }}>⋮⋮</span>
        <span class="hv-leaf__marker">◇</span>
        <span class="hv-leaf__label">
          <LeafInlineName leaf={leaf} editCtl={editCtl} />
          <span class="hv-leaf__meta muted">
            {leaf.displayName && <span class="hv-leaf__symbol mono">({leaf.symbol})</span>}
            <span class="hv-leaf__type-badge">{leaf.valueType}</span>
          </span>
        </span>
        <button type="button"
                class="hv-schema-edit__trash"
                title="Leaf löschen"
                onClick={() => editCtl.deleteEntity('leaf', leaf.id, leaf.displayName ?? leaf.symbol)}>🗑</button>
      </div>
    );
  }

  const sensitivityTone =
    leaf.readSensitivity === 'Critical' ? 'error' :
    leaf.readSensitivity === 'Elevated' ? 'warn' : null;
  // Pull the leaf-scoped signal *once*. LeafValueDisplay reads it via `.value`,
  // which means only THIS row re-renders when this leaf updates. 5000-leaf
  // trees stay fast because 4999 rows stay untouched on every batch.
  const valueSignal = live?.leafSignal(leaf.id) ?? null;
  const isWritable = leaf.writability === 'ReadWrite' || leaf.writability === 'WriteOnly';
  // Local expand state for the inline history chart. Kept per-row so the
  // page-level state stays lean and closing one chart doesn't re-render the
  // whole tree. Not persisted — a fresh visit starts collapsed.
  const [chartOpen, setChartOpen] = useState(false);
  // Only leaves that are numeric AND have a chance of historical data are
  // eligible for the mini-chart toggle. Bool/Enum/Flags leaves render fine
  // in the row but a time-series chart of them is nonsense.
  const chartable = leaf.valueType === 'Float' || leaf.valueType === 'Int' || leaf.valueType === 'Integer' || leaf.valueType === 'Number';
  return (
    <div class="hv-leaf-wrap">
      <div class="hv-leaf" style={{ paddingLeft: `${depth * 10}px` }}>
        <button
          type="button"
          class={`hv-leaf__chart-toggle${chartOpen ? ' hv-leaf__chart-toggle--on' : ''}${chartable ? '' : ' hv-leaf__chart-toggle--disabled'}`}
          onClick={() => chartable && setChartOpen((o) => !o)}
          aria-label={chartOpen ? 'Verlauf schließen' : 'Verlauf einblenden'}
          title={chartable ? (chartOpen ? 'Verlauf schließen' : 'Verlauf einblenden') : 'Kein Verlauf für diesen Typ'}
          disabled={!chartable}
        >
          {/* Mini bar-chart glyph — three ascending bars, drawn as SVG so it
              scales cleanly with the row and can be recoloured via `currentColor`. */}
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <rect x="1" y="7" width="2" height="4" fill="currentColor" />
            <rect x="5" y="4" width="2" height="7" fill="currentColor" />
            <rect x="9" y="1" width="2" height="10" fill="currentColor" />
          </svg>
        </button>
        {configCtl.canEdit ? (
          <button
            type="button"
            class="hv-leaf__config-toggle"
            onClick={() => configCtl.openFor(leaf)}
            aria-label="Datenpunkt konfigurieren"
            title="Datenpunkt konfigurieren"
          >
            {/* Wrench glyph, drawn same style as the chart glyph so the two
                icons feel like a set. */}
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M8 1.5a2.5 2.5 0 0 0-3 3.3L1.7 8.1v2.2h2.2l3.3-3.3a2.5 2.5 0 0 0 3.3-3l-1.6 1.6L7.6 4.4 9.2 2.8z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.1"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
            </svg>
          </button>
        ) : (
          // Occupies the grid slot so rows without edit permission still
          // align with rows that have the button.
          <span aria-hidden="true" />
        )}
        {editCtl?.editMode && (
          <span class="hv-schema-edit__handle hv-schema-edit__handle--leaf"
                title="Ziehen zum Verschieben"
                draggable
                onDragStart={(e) => {
                  if (!e.dataTransfer) return;
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('application/x-hvacloud-schema',
                    JSON.stringify({ kind: 'leaf', id: leaf.id }));
                }}>⋮⋮</span>
        )}
        <span class="hv-leaf__marker">◇</span>
        {/* Label column: display name first (primary), then symbol + type in
            a small muted meta group. Falls back to symbol alone when the
            leaf has no display name so the column never renders empty. */}
        <span class="hv-leaf__label">
          {editCtl?.editMode ? (
            <LeafInlineName leaf={leaf} editCtl={editCtl} />
          ) : (
            <span class="hv-leaf__name">{leaf.displayName ?? leaf.symbol}</span>
          )}
          <span class="hv-leaf__meta muted">
            {leaf.displayName && <span class="hv-leaf__symbol mono">({leaf.symbol})</span>}
            <span class="hv-leaf__type-badge">{leaf.valueType}</span>
          </span>
        </span>
        <LeafValueDisplay
          leaf={leaf}
          valueSignal={valueSignal}
          hydrationSignal={live?.hydrationSignal ?? null}
          history={live?.leafHistory(leaf.id) ?? null}
          settings={settings}
        />
      <div class="hv-leaf__badges">
        {sensitivityTone && (
          <StatusBadge tone={sensitivityTone} title={`Read sensitivity: ${leaf.readSensitivity}`}>
            {leaf.readSensitivity.toLowerCase()}
          </StatusBadge>
        )}
        {leaf.safetyMarker && <StatusBadge tone="error" title="Safety-relevant">safety</StatusBadge>}
        {/* Live alarm marker — reads activeAlarmsSignal internally, so only the
         *  marker (not the whole LeafRow) re-renders on alarm events. Cheap
         *  because the filter short-circuits on leafId mismatch and the active
         *  list stays small in practice (concept: ≤ hundreds). */}
        <LeafAlarmMarker leafId={leaf.id} />
      </div>
      {isWritable ? (
          <button
            type="button"
            class="hv-leaf__send"
            title="Wert schreiben…"
            aria-label={`Wert schreiben für ${leaf.symbol}`}
            onClick={() => modalCtl.openFor(leaf)}
          >
            ✎
          </button>
        ) : <span />}
      </div>
      {chartOpen && chartable && (
        <div class="hv-leaf-chart-wrap" style={{ paddingLeft: `${depth * 16}px` }}>
          <LeafHistoryChart
            leafId={leaf.id}
            leaf={leaf}
            onClose={() => setChartOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

// ---- Schema-Bearbeiten: Inline-Rename-Helper ------------------------------
//
// Kleines Input-Feld mit Enter=commit / Escape=cancel. Isoliert damit die
// Row selber nicht bei jedem Tastenanschlag neu rendert. Autofocus + Select
// beim Mount so der Operator sofort tippen kann.

function InlineRename({ initial, onCommit, onCancel }: {
  initial: string;
  onCommit(v: string): void;
  onCancel(): void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <input
      type="text"
      class="hv-schema-edit__rename"
      value={value}
      autoFocus
      ref={(el) => { if (el) el.select(); }}
      onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit(value);
        else if (e.key === 'Escape') onCancel();
      }}
      onBlur={() => value !== initial ? onCommit(value) : onCancel()}
    />
  );
}

// Leaf-spezifische Rename-Wrapper — Pencil-Icon + Rename-Input inline, damit
// der Leaf-Row-Grid nicht mit weiteren Buttons überladen wird.
function LeafInlineName({ leaf, editCtl }: { leaf: LeafDto; editCtl: EditCtl }) {
  const [renaming, setRenaming] = useState(false);
  if (renaming) {
    return (
      <InlineRename
        initial={leaf.displayName ?? leaf.symbol}
        onCommit={(v) => { setRenaming(false); void editCtl.renameDisplay('leaf', leaf.id, v); }}
        onCancel={() => setRenaming(false)} />
    );
  }
  return (
    <>
      <button type="button"
              class="hv-schema-edit__pencil"
              title="Anzeigename bearbeiten"
              onClick={() => setRenaming(true)}>✎</button>
      <span class="hv-leaf__name">{leaf.displayName ?? leaf.symbol}</span>
    </>
  );
}

// ---- Device-Root Drop Zone -----------------------------------------------
//
// Schmales Drop-Ziel oben in jeder Device-Card. Ein hierher gezogener Node
// wird per makeRoot=true zum direkten Kind des Devices; ein Leaf ist unter
// dem Device selbst nicht erlaubt (Leaves brauchen immer einen Parent-Node)
// und wird mit Toast abgelehnt statt via 400 vom Server.
function DeviceRootDropZone({ deviceId, editCtl }: { deviceId: string; editCtl: EditCtl }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const setActive = (on: boolean) => {
    const el = ref.current;
    if (!el) return;
    if (on) el.classList.add('hv-schema-edit__root-drop--active');
    else el.classList.remove('hv-schema-edit__root-drop--active');
  };
  return (
    <div ref={ref}
         class="hv-schema-edit__root-drop"
         data-device-id={deviceId}
         onDragOver={(e) => {
           if (!e.dataTransfer?.types.includes('application/x-hvacloud-schema')) return;
           e.preventDefault();
           e.dataTransfer.dropEffect = 'move';
           setActive(true);
         }}
         onDragLeave={(e) => {
           if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) return;
           setActive(false);
         }}
         onDrop={(e) => {
           setActive(false);
           const payload = e.dataTransfer?.getData('application/x-hvacloud-schema');
           if (!payload) return;
           e.preventDefault();
           try {
             const { kind, id } = JSON.parse(payload) as { kind: 'node' | 'leaf'; id: string };
             if (kind === 'leaf') {
               showToast({ tone: 'warn', title: 'Leaf braucht Parent-Node',
                 message: 'Leaves können nicht direkt unter dem Device liegen — ziehe stattdessen einen Node hierher.',
                 autoHideAfterMs: 5000 });
               return;
             }
             void editCtl.reparent('node', id, null);
           } catch { /* payload malformed — ignore */ }
         }}>
      ⇧ Hier ablegen um Node auf Root-Ebene zu heben
    </div>
  );
}
